import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { ArrowDownToLine, FileText, TriangleAlert, HelpCircle, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import Tree, { TreeNode, fileIconForName } from '../real-gain/rag/Tree/Tree';
import { buildStorageTree, nameOfPath } from '../real-gain/rag/Tree/storageTree';
import { useStorageTree } from '../real-gain/rag/Tree/useStorageTree';
import { PLATFORM_VOLUME_KEY, storageAPI } from '../StorageAPI';
import { authenticationAtom } from '../State';
import { useModalContext } from '../components/ModalProvider';
import { AnalysisRow, useDocumentAnalysisAPI } from '../DocumentAnalysisAPI';
import {
    DataRoomFile,
    dataRoomBadge,
    dataRoomFileLabel,
    dataRoomFileOf,
    dataRoomHint,
    moveDestination,
} from './dataRoomTree';
import { fileKind, formatFileDate, formatFileSize } from '../utilities/fileFacts';
import { DEFAULT_SHARE, clampShare, parseShare, shareFromPointerAlong } from '../utilities/splitPane';
import MissingDocuments, { MissingDocument } from './MissingDocuments';
import FileThumbnail, { previewKindOf } from './FileThumbnail';
import DocumentViewerDialog from '../components/DocumentViewerDialog';
import BoundObjectsPanel from './BoundObjectsPanel';
import { currentLocale } from '../components/numericFormat';
import { OpenDecision } from './documentImport';

/**
 * Der Datenraum eines Projekts: die Ablage als Ordnerbaum, die Dokumentenanalysen darüber.
 *
 * Der Baum gehört den Dateien. Ordner werden von Hand angelegt oder entstehen beim Entpacken eines
 * Zips; was eine Datei *ist*, steht in ihrer Analyse, und die hängt an der Id des verwalteten
 * Dokuments, nicht am Pfad. Deshalb übersteht ein Befund das Verschieben und Umbenennen — und
 * deshalb darf die Ablage die Struktur besitzen, ohne dass die Analysen davon wissen müssen.
 *
 * Eine Datei ohne Analyse ist kein Sonderfall. Jemand hat sie über die Dateiverwaltung abgelegt;
 * sie steht als gewöhnliche Datei da und ist normal löschbar. Umgekehrt ist eine Datei mit Analyse
 * gesperrt: Sie zu löschen ließe einen Befund über ein Dokument zurück, das es nicht mehr gibt.
 *
 * Gezeichnet wird mit demselben `Tree` wie die Dateiverwaltung und über denselben `useStorageTree`
 * — was hier anders ist, ist die Zeile und was daneben steht.
 */

interface Properties {
    /** Der Ordner des Projekts, wie ihn der Server aufgelöst hat. Ohne ihn gibt es nichts zu zeigen. */
    folderPath: string | null;
    /** Angestoßen, wenn sich etwas geändert hat, das andere Ansichten angeht. */
    onChanged?: () => void;
    /**
     * Dateien, die gerade hochgeladen werden — in der Ablage gibt es sie noch nicht.
     *
     * Sie stehen trotzdem im Baum, als Platzhalter: Wer eine Datei hineinzieht, will sie sehen,
     * nicht ein paar Sekunden Nichts und dann eine Zeile. Sobald der Server sie kennt, kommt sie
     * aus der Auflistung und der Platzhalter fällt weg.
     */
    pending?: string[];
    /** Der Fortschritt der laufenden Analysen, nach Auftrags-Id. */
    progress?: Record<string, { step?: string }>;
    /** Eine Analyse verwerfen — die Datei bleibt. Ohne Rückruf steht der Knopf nicht da. */
    onRemoveAnalysis?: (analysisId: string) => Promise<void> | void;
    /**
     * Ob Baum und Angaben übereinander oder nebeneinander stehen.
     *
     * Im Kamin des Dashboards übereinander: Die Spalte ist schmal, zwei Spalten darin hätten je gut
     * hundert Pixel, und der Baum braucht den Platz für seine Dateinamen. Über die volle Breite
     * einer Seite ist es umgekehrt — dort liegt die Datei neben ihrer Auskunft, und beide behalten
     * ihre Höhe.
     */
    orientation?: 'vertical' | 'horizontal';
    /**
     * Was mit gezogenen Dateien geschieht.
     *
     * In den Datenraum zu ziehen heißt immer: analysieren. Deshalb nicht der Weg über die Ablage,
     * der nur hochlädt — hier läuft derselbe Weg wie früher über die Ablagefläche, und die Datei
     * landet dabei im Projektordner.
     */
    onAnalyse?: (files: File[], folderPath?: string) => Promise<void> | void;
    /** Wird der Baum in einer Hülle gezeichnet, die schon Rahmen und Höhe mitbringt? */
    embedded?: boolean;
    /**
     * Dokumente, die diese Phase verlangt und die noch nicht da sind.
     *
     * Sie stehen als blasse Zeilen zwischen den Dateien. Der Datenraum zeigt sonst nur, was da ist
     * — und die Frage, die vor einer Prüfung steht, ist die umgekehrte. Wer sie beantworten will,
     * müsste sonst die Anforderungsliste danebenlegen und abhaken.
     */
    missing?: MissingDocument[];
    /** Offene Entscheidungen je Analyse. */
    decisions?: Record<string, OpenDecision[]>;
    /**
     * Die Läufe mit einem Plan, der noch übernommen werden kann — nach Dokument-Id.
     *
     * Ohne diese beiden bleibt die Ansicht, was sie war: Sie zeigt Befunde und ändert nichts. Erst
     * wer sie mitgibt, macht aus dem Dateimanager die Stelle, an der ein Dokument in den Bestand
     * kommt — und das ist Absicht, denn nur hier soll das gehen.
     */
    awaitingMerge?: Record<string, AnalysisRow>;
    onMerge?: (analysis: AnalysisRow) => void;
    /** Eine Entscheidung beantworten. */
    onDecide?: (decision: OpenDecision) => void;
}

/** Wo der Stand des Trenners liegt, damit er eine Sitzung übersteht. */
const SHARE_STORAGE_KEY = 'dataRoom.detailShare';

/** Wie ein Zustand aussieht — Farbe und Wort in einem, damit die Zeile nicht zweimal erklärt wird. */
const STATE_STYLE: Record<DataRoomFile['state'], { variant: any; key: string } | null> = {
    plain: null,
    running: { variant: 'warning', key: 'portfolioDashboard.dataRoom.running' },
    analysed: { variant: 'success', key: 'portfolioDashboard.dataRoom.analysed' },
    failed: { variant: 'destructive', key: 'portfolioDashboard.dataRoom.failed' },
};

export default function DataRoomView({
    folderPath,
    onChanged,
    pending,
    missing,
    progress,
    onRemoveAnalysis,
    onAnalyse,
    embedded,
    decisions,
    onDecide,
    awaitingMerge,
    onMerge,
    orientation = 'vertical',
}: Properties) {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { pushError } = useModalContext() as any;
    const documentAnalysisApi = useDocumentAnalysisAPI();

    /** Nebeneinander statt übereinander — bestimmt Achse, Trenner und Tastenbelegung. */
    const waagerecht = orientation === 'horizontal';

    const [analyses, setAnalyses] = useState<Record<string, AnalysisRow>>({});
    const [selected, setSelected] = useState<any | null>(null);
    /**
     * Läuft gerade ein Anstoßen? Nur um den zweiten Wurf abzuwehren, nicht um es anzuzeigen — der
     * Zustand einer Datei steht an ihrer Zeile.
     */
    /** Ein Anstoßen läuft. Nur zur Abwehr des zweiten Wurfs — angezeigt wird der Zustand je Zeile. */
    const [busy, setBusy] = useState(false);
    /** Wohin gezogen wurde — dort steht der Platzhalter, und dort landet die Datei. */
    const [pendingFolder, setPendingFolder] = useState('');
    /** Trigger to force-refresh the analysis overlay cache. */
    const [analysisRefreshKey, setAnalysisRefreshKey] = useState(0);

    /**
     * Wie viel von der Höhe die Angaben bekommen — verschiebbar, und über die Sitzung hinaus gemerkt.
     *
     * Eine feste Aufteilung kann nur eine von zwei Arbeitsweisen bedienen: Wer sich durch einen
     * tiefen Ordnerbaum liest, braucht die Höhe oben; wer ein PDF ansieht, braucht sie unten. Der
     * Anteil wird gemerkt, weil ein Trenner, der bei jedem Aufruf zurückspringt, bei jedem Aufruf
     * neu gezogen werden muss.
     */
    const splitRef = useRef<HTMLDivElement | null>(null);
    const [detailShare, setDetailShare] = useState(() => {
        try {
            return parseShare(window.localStorage.getItem(SHARE_STORAGE_KEY), DEFAULT_SHARE);
        } catch {
            // Ein Browser ohne zugänglichen Speicher ist kein Grund, die Ansicht nicht zu zeichnen.
            return DEFAULT_SHARE;
        }
    });
    const [dragging, setDragging] = useState(false);

    /**
     * Das Dokument in echt, wenn jemand auf die Vorschau klickt.
     *
     * Der Verweis kommt vom Vorschaubild, das die Datei ohnehin geholt hat. Beim Wechsel der
     * gewählten Datei fällt er weg — ein Betrachter, der noch das vorige Dokument zeigt, während
     * daneben ein anderes markiert ist, wäre schlimmer als keiner.
     */
    const [viewerUrl, setViewerUrl] = useState<string | null>(null);
    /** Der jeweils letzte Anteil, um ihn beim Loslassen zu merken, ohne bei jedem Zucken zu schreiben. */
    const shareRef = useRef(detailShare);
    shareRef.current = detailShare;

    const rememberShare = useCallback(() => {
        try {
            window.localStorage.setItem(SHARE_STORAGE_KEY, String(shareRef.current));
        } catch {
            // Nicht merken zu können ist ein Schönheitsfehler, kein Fehler.
        }
    }, []);

    /**
     * Der Zeiger wird auf den Griff festgelegt.
     *
     * Sonst verliert das Ziehen den Faden, sobald der Zeiger den drei Pixel schmalen Griff verlässt
     * — und über einem `iframe` mit der PDF-Vorschau kommt er ohnehin nicht mehr zurück, weil dessen
     * Ereignisse in einem anderen Dokument landen.
     */
    const startDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
    }, []);

    const moveDrag = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            if (!dragging) return;

            const bounds = splitRef.current?.getBoundingClientRect();
            if (!bounds) return;

            // Dieselbe Rechnung, nur entlang der Achse, in der geteilt wird.
            const next = waagerecht
                ? shareFromPointerAlong(event.clientX, { start: bounds.left, extent: bounds.width })
                : shareFromPointerAlong(event.clientY, { start: bounds.top, extent: bounds.height });
            // `null` heißt: Der Behälter hat gerade keine Ausdehnung. Dann bleibt der Stand.
            if (next !== null) setDetailShare(next);
        },
        [dragging, waagerecht],
    );

    const endDrag = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            if (!dragging) return;

            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId);
            }
            setDragging(false);
            rememberShare();
        },
        [dragging, rememberShare],
    );

    /** Mit den Pfeiltasten, damit der Trenner nicht nur der Maus gehört. */
    const nudge = useCallback(
        (event: ReactKeyboardEvent<HTMLDivElement>) => {
            // Die Taste, die den hinteren Bereich vergrößert, zeigt zu ihm hin: nach oben bei
            // übereinander, nach links bei nebeneinander.
            const vergroessern = waagerecht ? 'ArrowLeft' : 'ArrowUp';
            const verkleinern = waagerecht ? 'ArrowRight' : 'ArrowDown';
            const step = event.key === vergroessern ? 0.05 : event.key === verkleinern ? -0.05 : 0;
            if (!step) return;

            event.preventDefault();
            // Außerhalb des Aktualisierers gerechnet: `rememberShare` liest `shareRef` sofort, der
            // Aktualisierer liefe erst beim Verarbeiten der Änderung — gemerkt würde der vorige Stand.
            const next = clampShare(shareRef.current + step);
            shareRef.current = next;
            setDetailShare(next);
            rememberShare();
        },
        [rememberShare, waagerecht],
    );

    /**
     * Die Knoten des Baums.
     *
     * Angezeigt wird der letzte Abschnitt, gespeichert der volle Pfad: Jede Aktion spricht mit der
     * Ablage, und die kennt nur ganze Pfade. Weil die Auflistung schon beim Projektordner anfängt,
     * fängt auch der Baum dort an — kürzen muss hier niemand.
     *
     * Die Rückrufe hängen am Knoten, weil das Zeilenmenü sie dort sucht. Ohne sie stünden
     * Umbenennen, Löschen und Herunterladen als tote Einträge im Menü.
     */
    /**
     * In Referenzen, nicht in den Abhängigkeiten des Erbauers.
     *
     * Der Baum wird beim Auflisten gebaut, die Abzeichen ändern sich zwischendurch — hinge der
     * Erbauer daran, würde jeder Fortschrittsschritt eine Neuauflistung auslösen. Stattdessen liest
     * er den jeweils aktuellen Stand, und ein eigener Effekt zeichnet die Abzeichen nach.
     */
    const overlayRef = useRef({ analyses, progress, t, decisions, awaitingMerge });
    overlayRef.current = { analyses, progress, t, decisions, awaitingMerge };

    /**
     * Die Aktionen über eine Referenz, nicht über die Abhängigkeiten des Erbauers.
     *
     * `buildNodes` darf sich nicht bei jedem Zeichnen ändern — sonst baut der Haken den Baum neu.
     * Es die Rückrufe des ersten Durchlaufs festhalten zu lassen war aber der schlimmere Fehler:
     * Beim ersten Durchlauf ist der Datenraum noch nicht aufgelöst, und ein Anstoßen ohne Datenraum
     * steigt stillschweigend aus. Ein Abwurf auf einen Ordner tat dann gar nichts — keine Analyse,
     * kein Fortschritt, keine Meldung.
     */
    const actionsRef = useRef<any>({});


    const badgeFor = useCallback((documentId?: string) => {
        const { analyses: rows, progress: runs, t: translate } = overlayRef.current;
        const analysis: any = documentId ? rows[documentId] : undefined;
        const step = analysis?.jobId ? runs?.[analysis.jobId]?.step : undefined;

        return dataRoomBadge(dataRoomFileOf(analysis), step ? translate(`documentAnalysisPanel.test.steps.${step}`) : undefined, {
            running: translate('portfolioDashboard.dataRoom.running'),
            analysed: translate('portfolioDashboard.dataRoom.analysed'),
            failed: translate('portfolioDashboard.dataRoom.failed'),
        });
    }, []);

    /**
     * Die offenen Entscheidungen einer Datei — über die Referenz, nicht über die Abhängigkeiten.
     *
     * Aus demselben Grund wie `badgeFor`: Der Erbauer des Baums darf sich nicht ändern, sonst listet
     * jede eingehende Entscheidung neu auf. Hinge er stattdessen an den Abhängigkeiten *und* bliebe
     * bei `[]`, hielte er die Entscheidungen des ersten Durchlaufs fest — und die sind immer leer.
     */
    const decisionsFor = useCallback((documentId?: string): OpenDecision[] => {
        const { analyses: rows, decisions: open } = overlayRef.current;
        if (!documentId || !open) return [];

        const analysis = rows[documentId];

        return analysis ? open[analysis._id] ?? [] : [];
    }, []);

    /**
     * Das zweite Abzeichen: Hier ist noch etwas zu tun.
     *
     * Zwei Dinge teilen sich den Platz, und die Reihenfolge ist eine Aussage. Eine offene Frage geht
     * vor: Sie ist der Grund, aus dem ein Teil des Plans nicht angewendet werden kann, und wer sie
     * zuerst beantwortet, übernimmt hinterher mehr. Ein zweites Abzeichen daneben würde beide
     * kleiner machen, ohne etwas zu sagen, das die Auswahl nicht ohnehin zeigt.
     */
    const decisionBadgeFor = useCallback(
        (documentId?: string) => {
            const count = decisionsFor(documentId).length;

            if (count > 0) {
                return {
                    text: overlayRef.current.t('portfolioDashboard.dataRoom.decisions', { count }),
                    tone: 'warn' as const,
                };
            }

            const awaiting = documentId ? overlayRef.current.awaitingMerge?.[documentId] : undefined;

            return awaiting
                ? { text: overlayRef.current.t('portfolioDashboard.dataRoom.mergeReady'), tone: 'info' as const }
                : undefined;
        },
        [decisionsFor],
    );

    const buildNodes = useCallback(
        (listing: any) =>
            buildStorageTree(listing.items ?? [], {
                createFile: (item: any) => {
                    const documentId = item._id ? String(item._id) : undefined;
                    const badge = badgeFor(documentId);

                    return {
                        name: nameOfPath(item.path),
                        type: 'file' as const,
                        path: item.path,
                        fileId: documentId,
                        // Der ganze Eintrag, nicht nur Pfad und Id: Größe, Zeitpunkt und
                        // Zusammenfassung stehen schon darauf und wären sonst ein zweiter Abruf.
                        badge,
                        // Derselbe Ton wie das Abzeichen: Ein Punkt am Symbol sagt „hier hängt eine
                        // Analyse", die Farbe sagt, wie weit sie ist. Wo keiner sitzt, ist es eine
                        // gewöhnliche Datei.
                        iconMark: badge ? { tone: badge.tone } : undefined,
                        secondaryBadge: decisionBadgeFor(documentId),
                        /**
                         * Unmittelbar, ohne Vermittlung.
                         *
                         * Hier stand zwischenzeitlich ein Umweg über eine Referenz und eine Karte
                         * nach Pfad — beides gedacht, um immer die aktuelle Funktion zu greifen.
                         * `setSelected` ist aber schon stabil: React ändert den Setzer einer
                         * Zustandsgröße nie. Der Umweg löste ein Problem, das es nicht gab, und
                         * kostete die Gewissheit, dass ein Klick ankommt.
                         */
                        onClick: () => setSelected({ ...item, documentId }),
                        /**
                         * Der volle Name und ein Blick in den Inhalt, am Mauszeiger.
                         *
                         * Der Baum steht in einer schmalen Spalte; ein Dateiname überlebt das
                         * selten ganz, und „Mietvertrag_Muell…" unterscheidet sich von
                         * „Mietvertrag_Mueller_2019…" nicht. Die Kurzfassung entsteht ohnehin beim
                         * Aufbereiten für die Suche und steht am Dokument — hier kostet sie nichts.
                         */
                        hint: dataRoomHint(item.path, item.summary),
                        onRename: (name: string) => actionsRef.current.renameFile(item.path, name),
                        onDelete: (done: () => void) => void actionsRef.current.removeObject(item.path, done),
                        onDownload: () => void actionsRef.current.download(item.path),
                    };
                },
                createFolder: (path: string, name: string, files: TreeNode[]) => ({
                    name,
                    type: 'folder' as const,
                    path,
                    files,
                    onRename: (newName: string) => actionsRef.current.renameFolder(path, newName),
                    onDelete: (done: () => void) => void actionsRef.current.removeObject(path, done),
                    onFileDrop: (files_: File[]) => actionsRef.current.upload(files_, path),
                    // Eine Datei aus dem Baum auf diesen Ordner: verschieben, nicht auswerten.
                    // `fileItem` ist der Eintrag, den der Baum zieht — `{ id, name, path, type }`,
                    // kein Pfad.
                    onFileMove: (fileItem: any) => actionsRef.current.moveFile(fileItem, path),
                    onCreateFolder: (childName: string, done: () => void) => {
                        void actionsRef.current.createFolderAt(`${path}${childName}`).then(done);
                    },
                    onToggleOpen: (isOpen: boolean) => actionsRef.current.setFolderOpen(path, isOpen),
                }),
            }),
        // Die Aktionen kommen aus `actionsRef` und sind deshalb immer die aktuellen, ohne dass
        // der Erbauer sich ändert.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const tree = useStorageTree({
        customer: authentication?.customer,
        volume: PLATFORM_VOLUME_KEY,
        basePath: folderPath ?? undefined,
        buildNodes,
        onError: pushError,
    });

    const { refresh } = tree;

    useEffect(() => {
        if (folderPath) void refresh({ force: true });
    }, [folderPath, refresh]);

    /**
     * Die Überlagerung, für den ganzen Ordner auf einmal.
     *
     * Eine Anfrage je Ansicht statt einer je Zeile — und sie darf leer ausgehen: Wo nichts
     * zurückkommt, sind es eben Dateien ohne Analyse.
     */
    const documentIds = useMemo(() => {
        const ids: string[] = [];
        const walk = (nodes: TreeNode[]) =>
            nodes.forEach((node) => {
                if (node.type === 'file' && node.fileId) ids.push(node.fileId);
                if (node.files) walk(node.files);
            });

        walk(tree.data);
        return ids;
    }, [tree.data]);

    const documentKey = documentIds.join(',');

    /**
     * Die Abzeichen nachzeichnen, wenn sich Analysen oder Fortschritt ändern.
     *
     * Ohne das stünde am Baum der Stand des letzten Auflistens: Eine Analyse, die gerade von
     * „Konvertierung" auf „Typbestimmung" springt, ändert nichts an der Ablage — und genau diese
     * Zustandswechsel will der Leser sehen.
     */
    const progressKey = JSON.stringify(progress ?? {});

    useEffect(() => {
        tree.setData((current) => {
            const paint = (nodes: TreeNode[]): TreeNode[] =>
                nodes.map((node) => {
                    if (node.type !== 'file') {
                        return { ...node, files: node.files ? paint(node.files) : node.files };
                    }

                    const badge = badgeFor(node.fileId);

                    // Zeichen und Abzeichen gehören zusammen — eines ohne das andere nachzuziehen
                    // hieße, dass der Punkt am Symbol den vorigen Zustand zeigt.
                    return {
                        ...node,
                        badge,
                        iconMark: badge ? { tone: badge.tone } : undefined,
                        secondaryBadge: decisionBadgeFor(node.fileId),
                    };
                });

            return paint(current);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analyses, progressKey, decisions, awaitingMerge]);

    /**
     * Während ein Lauf läuft, neu auflisten.
     *
     * Der Lauf legt die Datei früh ab und arbeitet dann weiter. Ohne dieses Nachfassen bliebe der
     * Platzhalter stehen, bis jemand von Hand aktualisiert — die echte Zeile mit ihrem Fortschritt
     * gäbe es längst. Die Entprellung im Haken sorgt dafür, dass daraus keine Anfrageflut wird.
     */
    useEffect(() => {
        if (!folderPath || !progressKey || progressKey === '{}') return;

        void refresh();
        setAnalysisRefreshKey((k) => k + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [progressKey, folderPath]);

    /**
     * Was gerade hochlädt, steht als Platzhalter oben im Baum.
     *
     * Die Ablage kennt diese Dateien noch nicht; sie erst nach dem Hochladen zu zeigen hieße, dass
     * eine gezogene Datei ein paar Sekunden lang verschwindet. Der Platzhalter fällt von selbst
     * weg, sobald die Auflistung sie enthält.
     */
    const nodes = useMemo(() => {
        const inTree = new Set<string>();
        const walk = (list: TreeNode[]) =>
            list.forEach((node) => {
                if (node.type === 'file') inTree.add(node.name);
                if (node.files) walk(node.files);
            });
        walk(tree.data);

        const placeholders: TreeNode[] = (pending ?? [])
            .filter((name) => !inTree.has(name))
            .map((name) => ({
                name,
                type: 'file' as const,
                path: `pending:${name}`,
                // Dieselbe Zeile wie später die Datei, nur blass — mit demselben Symbol, an
                // derselben Stelle. Was mit ihr geschieht, steht daneben.
                dimmed: true,
                badge: { text: t('portfolioDashboard.dataRoom.uploading'), tone: 'info' as const, busy: true },
            }));


        if (placeholders.length === 0) return tree.data;

        const byName = (left: TreeNode, right: TreeNode) =>
            left.name.localeCompare(right.name, undefined, { numeric: true });

        /**
         * In den Ordner, auf den gezogen wurde.
         *
         * Dort landet die Datei auch — der Lauf bekommt denselben Pfad. Sie stattdessen oben zu
         * zeigen hieße, dass die Zeile beim Auftauchen in einen anderen Ordner springt, und dann
         * ist der Zustand, den sie trägt, nicht mehr zu verfolgen.
         */
        if (pendingFolder && pendingFolder !== folderPath) {
            const into = (list: TreeNode[]): TreeNode[] =>
                list.map((node) =>
                    node.type === 'folder' && node.path === pendingFolder
                        ? {
                              ...node,
                              isOpen: true,
                              files: [...(node.files ?? []), ...placeholders].sort(byName),
                          }
                        : { ...node, files: node.files ? into(node.files) : node.files },
                );

            return into(tree.data);
        }

        /**
         * Der Platzhalter steht dort, wo die Datei liegen wird — im Projektordner, unter den
         * anderen Dateien, nach Namen einsortiert.
         *
         * Ihn oben anzuhängen wäre einfacher und falsch: Die Zeile spränge, sobald der Server die
         * Datei kennt und sie an ihren Platz rutscht. Wer den Zustand verfolgt, verlöre sie dabei
         * aus dem Blick — und der Zustand ist der einzige Grund, warum sie überhaupt schon dasteht.
         */
        const files = tree.data.filter((node) => node.type === 'file');
        const folders = tree.data.filter((node) => node.type !== 'file');

        return [...[...files, ...placeholders].sort(byName), ...folders];
    }, [tree.data, pending, pendingFolder, folderPath]);

    useEffect(() => {
        if (documentIds.length === 0) {
            setAnalyses({});
            return;
        }

        let cancelled = false;

        documentAnalysisApi
            .analysesByFiles(documentIds)
            .then((result) => {
                if (!cancelled) setAnalyses(result.analyses ?? {});
            })
            .catch((error: any) => console.error('Could not load the analyses of the data room', error));

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentKey, analysisRefreshKey]);

    /**
     * Hochladen, wohin gezogen wurde.
     *
     * Der Zielordner ist der volle Pfad; ein Zip wird serverseitig entpackt und legt dabei echte
     * Ordner an — genau deshalb gehört die Struktur der Ablage und nicht einer Nebentabelle.
     */
    /**
     * Gezogene Dateien werden analysiert.
     *
     * Nicht bloß abgelegt: Wer etwas in den Datenraum zieht, will wissen, was drinsteht. Der
     * Analyselauf legt die Datei dabei selbst in den Projektordner — deshalb genügt hier ein
     * Anstoßen und ein Neuauflisten danach.
     *
     * Der Ordner, auf den gezogen wurde, geht mit: Der Lauf legt die Datei dort ab, sofern er im
     * Projektordner liegt — der Server prüft das und fällt sonst auf den Projektordner zurück.
     */
    const upload = useCallback(
        async (files: File[], folder: string) => {
            if (files.length === 0 || busy) return;

            setBusy(true);
            setPendingFolder(folder || folderPath || '');
            try {
                await onAnalyse?.(files, folder || undefined);
                await refresh({ force: true });
                onChanged?.();
            } catch (error) {
                pushError(error);
            } finally {
                setBusy(false);
            }
        },
        [onAnalyse, refresh, onChanged, pushError, busy, folderPath],
    );

    const createFolderAt = useCallback(
        async (fullPath: string) => {
            if (!authentication?.customer) return;

            try {
                await storageAPI.createFolder(authentication.customer, PLATFORM_VOLUME_KEY, fullPath);
                toast.success(t('portfolioDashboard.dataRoom.folderCreated', { name: nameOfPath(fullPath) }));
                await refresh({ force: true });
            } catch (error) {
                pushError(error);
            }
        },
        [authentication?.customer, refresh, pushError, t],
    );

    /** Ein Ordner unmittelbar im Projektordner — was der Knopf über dem Baum anlegt. */
    const createFolder = useCallback(
        (name: string) => createFolderAt(`${folderPath ?? ''}${name}`),
        [createFolderAt, folderPath],
    );

    const renameFile = useCallback(
        async (path: string, name: string) => {
            if (!authentication?.customer) return;

            try {
                await storageAPI.renameFile(authentication.customer, PLATFORM_VOLUME_KEY, path, name);
                await refresh({ force: true });
            } catch (error) {
                pushError(error);
            }
        },
        [authentication?.customer, refresh, pushError],
    );

    const renameFolder = useCallback(
        async (path: string, name: string) => {
            if (!authentication?.customer) return;

            try {
                await storageAPI.renameFolder(authentication.customer, PLATFORM_VOLUME_KEY, path, name);
                await refresh({ force: true });
            } catch (error) {
                pushError(error);
            }
        },
        [authentication?.customer, refresh, pushError],
    );

    /**
     * Löschen — und die Absage annehmen, wenn eine Analyse daran hängt.
     *
     * Die Sperre sitzt im Server, weil dieselbe Datei auch über die Dateiverwaltung erreichbar ist.
     * Hier kommt sie als Fehler zurück, und der Text nennt den Grund; die Zeile bleibt dann stehen,
     * statt zu verschwinden und beim nächsten Auflisten wieder aufzutauchen.
     */
    const removeObject = useCallback(
        async (path: string, done?: () => void) => {
            if (!authentication?.customer) return;

            try {
                await storageAPI.deleteObject(authentication.customer, PLATFORM_VOLUME_KEY, path);
                done?.();

                /**
                 * Die Auswahl fällt mit dem Gelöschten weg.
                 *
                 * Beim Verschieben zieht sie mit; beim Löschen blieb sie stehen, und die Angaben
                 * daneben zeigten weiter eine Datei, die es nicht mehr gibt — samt Vorschau, Zustand
                 * und Schaltflächen, die auf einen Pfad zeigen, der ins Leere läuft.
                 *
                 * Auch was *darin* lag: Ein Ordner wird mitsamt Inhalt entfernt, und die gewählte
                 * Datei kann eine davon sein. Ordnerpfade tragen einen Schrägstrich am Ende, Dateien
                 * nicht — deshalb der Vergleich in zwei Teilen und nicht als blosses `startsWith`,
                 * das sonst „Vertrag" auch in „Vertragsentwurf.pdf" fände.
                 *
                 * Über den Aktualisierer statt über `selected`, wie beim Verschieben: Was hier gilt,
                 * ist der Stand von jetzt, nicht der vom Zeichnen.
                 */
                const geloeschterOrdner = path.endsWith('/') ? path : `${path}/`;

                setSelected((current: any) =>
                    current?.path === path || String(current?.path ?? '').startsWith(geloeschterOrdner)
                        ? null
                        : current,
                );

                await refresh({ force: true });
                onChanged?.();
            } catch (error) {
                pushError(error);
                await refresh({ force: true });
            }
        },
        [authentication?.customer, refresh, onChanged, pushError],
    );

    const download = useCallback(
        async (path: string) => {
            if (!authentication?.customer) return;

            try {
                const { blob, filename } = await storageAPI.downloadFile(
                    authentication.customer,
                    PLATFORM_VOLUME_KEY,
                    path,
                );
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename || nameOfPath(path);
                link.click();
                URL.revokeObjectURL(url);
            } catch (error) {
                pushError(error);
            }
        },
        [authentication?.customer, pushError],
    );

    /**
     * Eine Datei innerhalb des Datenraums verschieben.
     *
     * `fileItem` ist der Eintrag, den der Baum zieht — `{ id, name, path, type }`. Ihn als Pfad zu
     * behandeln geht nicht auf: Der Aufruf käme mit einem Objekt an, wo der Server eine Zeichenkette
     * erwartet, und schon `nameOfPath` würde daran scheitern.
     *
     * Der Befund bleibt der Datei erhalten, denn er hängt an ihrer Id und nicht am Pfad — das ist
     * der Grund, warum sich hier überhaupt gefahrlos verschieben lässt.
     */
    const moveFile = useCallback(
        async (fileItem: any, targetFolderPath: string) => {
            const sourcePath = typeof fileItem === 'string' ? fileItem : fileItem?.path;
            if (!authentication?.customer || !sourcePath) return;

            const destination = moveDestination(sourcePath, targetFolderPath, folderPath ?? '');
            // `null`: derselbe Ordner, also nichts zu tun.
            if (!destination) return;

            try {
                await storageAPI.moveFile(authentication.customer, PLATFORM_VOLUME_KEY, sourcePath, destination);

                /**
                 * Die Auswahl zieht mit.
                 *
                 * Sonst zeigten die Angaben daneben auf einen Pfad, den es nicht mehr gibt — die
                 * Vorschau liefe ins Leere, und die markierte Zeile wäre eine andere als die
                 * gemeinte. Über den Aktualisierer statt über `selected`: Verschoben wird
                 * währenddessen, und was hier gilt, ist der Stand von jetzt und nicht der vom
                 * Zeichnen.
                 */
                setSelected((current: any) =>
                    current?.path === sourcePath ? { ...current, path: destination } : current,
                );

                await refresh({ force: true });
                onChanged?.();
            } catch (error) {
                pushError(error);
            }
        },
        // `selected` steht bewusst nicht dabei: Der Aktualisierer oben liest den aktuellen Stand
        // selbst, und die Abhängigkeit ließe diese Funktion bei jeder Auswahl neu entstehen.
        [authentication?.customer, refresh, onChanged, pushError, folderPath],
    );

    // Immer die Aktionen dieses Durchlaufs. Ohne das griffe der Baum auf die des ersten zu — und
    // beim ersten gab es noch keinen Datenraum.
    actionsRef.current = {
        upload,
        createFolderAt,
        renameFile,
        renameFolder,
        removeObject,
        download,
        moveFile,
        setFolderOpen: tree.updateFolderState,
    };

    /**
     * Eine andere Datei zu wählen schließt den Betrachter.
     *
     * Als Wirkung der Auswahl, nicht als Teil des Klicks: Was beim Anklicken geschieht, soll so
     * wenig wie möglich sein — sonst hängt die Auswahl an Dingen, die mit ihr nichts zu tun haben.
     */
    useEffect(() => {
        setViewerUrl(null);
    }, [selected?.path]);

    /** Der Zustand einer Zeile — aus der Überlagerung, nicht aus dem Baum. */
    const fileOf = useCallback(
        (documentId?: string) => dataRoomFileOf(documentId ? analyses[documentId] : undefined),
        [analyses],
    );

    const selectedFile = fileOf(selected?.documentId);
    const selectedLabel = dataRoomFileLabel(selectedFile);
    const selectedDecisions = decisionsFor(selected?.documentId);
    const selectedMerge = selected?.documentId ? awaitingMerge?.[selected.documentId] : undefined;

    /**
     * Ob es überhaupt etwas zu zeigen gibt — hier und nicht erst im Vorschaubild.
     *
     * Der Bereich für die Vorschau nimmt den freien Platz. Ihn auch dann aufzuspannen, wenn nichts
     * darin steht, hieße: eine leere Fläche über den Angaben, bei jeder Tabelle und jedem Word-
     * Dokument.
     */
    const previewKind = selected ? previewKindOf(selected.path, selected.size) : null;

    /** Art, Größe, Zeitpunkt — je Angabe eine Zeile, und nur wo es eine gibt. */
    const facts: Array<[string, string]> = selected
        ? ([
              [t('portfolioDashboard.dataRoom.kind'), fileKind(nameOfPath(selected.path))],
              [t('portfolioDashboard.dataRoom.size'), formatFileSize(selected.size)],
              [
                  t('portfolioDashboard.dataRoom.modified'),
                  formatFileDate(selected.lastModified ?? selected.updated, currentLocale()),
              ],
          ].filter(([, value]) => !!value) as Array<[string, string]>)
        : [];

    if (!folderPath) {
        return (
            <div className="rounded-lg border p-4 text-sm text-muted-foreground">
                {t('portfolioDashboard.dataRoom.noFolder')}
            </div>
        );
    }

    /**
     * Untereinander, nicht nebeneinander.
     *
     * Der Kamin ist eine schmale Spalte — zwei Spalten darin hätten je gut hundert Pixel, und der
     * Baum bräuchte den Platz für seine Dateinamen. Was über die gewählte Datei bekannt ist, steht
     * deshalb unter dem Baum und nicht daneben.
     *
     * Höhen werden begrenzt statt gesetzt: `h-full` in einem Elternteil ohne feste Höhe fällt auf
     * null zusammen, und genau das war hier der Fall. `max-h` mit eigenem Bildlauf wächst mit dem
     * Inhalt und hört rechtzeitig auf.
     */
    return (
        <div className={cn('flex min-w-0 flex-col gap-2', embedded && 'h-full', dragging && 'select-none')}>
            {/* Was fehlt, steht über dem Raum und nicht darin: Der Baum ist eine Ablage, ein
                fehlendes Dokument hat aber weder Pfad noch Ordner noch Datei. Als Zeile zwischen
                den anderen nähme es genau die Eigenschaften an, die es nicht hat. */}
            <MissingDocuments documents={missing ?? []} dense={embedded} />

            {/* Der geteilte Bereich. Was fehlt, steht darüber und nicht darin — der Trenner
                verteilt nur, was zwischen Ablage und Auskunft zu verteilen ist. */}
            <div
                ref={splitRef}
                className={cn(
                    'flex min-w-0 gap-2',
                    waagerecht ? 'flex-row' : 'flex-col',
                    embedded && 'min-h-0 flex-1',
                )}
            >

            {/* Feste Höhe, eigener Bildlauf: Der Baum füllt seinen Behälter und hat selbst keine
                Höhe. Ohne Begrenzung fällt er in einem Raster entweder auf null zusammen oder
                schiebt die Seite auf — dieselbe Hülle, die die Dateiverwaltung ihm gibt. */}
            <div
                className={cn(
                    'flex min-w-0 flex-col overflow-hidden',
                    embedded && 'min-h-0 flex-1',
                    !embedded && 'rounded-lg border',
                    // Nebeneinander teilt sich die Breite: Der Baum nimmt, was der Trenner ihm
                    // lässt, und behält seine eigene Höhe.
                    waagerecht && 'min-h-0 flex-1',
                )}
            >
                {/* Weder Kopf noch Fortschrittsbalken: Der Baum bringt seinen Kopf mit, und der
                    Zustand einer Datei gehört an ihre Zeile. Ein Balken darüber sagt, dass
                    irgendetwas läuft — die Frage ist aber, was mit welcher Datei. */}

                <div className={cn(
                    'overflow-y-auto',
                    embedded || waagerecht ? 'min-h-0 flex-1' : 'max-h-[26rem]',
                    embedded ? 'py-1' : 'p-3',
                )}>
                    <Tree
                        dense={embedded}
                        title={t('portfolioDashboard.dataRoom.title')}
                        data={nodes}
                        isLoading={tree.isInitialLoading}
                        selectedPath={selected?.path}
                        onFileDrop={upload}
                        onCreateRootFolder={createFolder}
                        onRefresh={() => refresh({ force: true })}
                        // Der Wurf auf die freie Fläche neben dem Baum: zurück in den Projektordner.
                        onFileMove={async (fileItem: any) => moveFile(fileItem, folderPath ?? '')}
                    />
                </div>
            </div>

            {/* Der Trenner.
                Nur wo die Spalte eine echte Höhe hat — außerhalb wächst die Ansicht mit ihrem
                Inhalt, und dann gibt es keine Höhe zu verteilen. */}
            {embedded || waagerecht ? (
                <div
                    role="separator"
                    // Ein Trenner ist quer zu dem, was er teilt: Bei nebeneinanderliegenden
                    // Bereichen steht er senkrecht.
                    aria-orientation={waagerecht ? 'vertical' : 'horizontal'}
                    aria-label={t('portfolioDashboard.dataRoom.resize')}
                    aria-valuenow={Math.round(detailShare * 100)}
                    aria-valuemin={15}
                    aria-valuemax={85}
                    tabIndex={0}
                    onPointerDown={startDrag}
                    onPointerMove={moveDrag}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onKeyDown={nudge}
                    className={cn(
                        'group relative flex shrink-0 items-center justify-center',
                        'touch-none outline-none focus-visible:ring-1 focus-visible:ring-ring',
                        waagerecht ? '-mx-1 w-3 cursor-col-resize' : '-my-1 h-3 cursor-row-resize',
                    )}
                >
                    <div
                        className={cn(
                            'rounded-full bg-border transition-colors',
                            waagerecht ? 'h-10 w-1' : 'h-1 w-10',
                            'group-hover:bg-primary/50 group-focus-visible:bg-primary/50',
                            dragging && 'bg-primary/60',
                        )}
                    />
                </div>
            ) : (
                <></>
            )}

            {/* Das Detailpanel: was über die gewählte Datei bekannt ist — und nichts, wo nichts ist. */}
            {/* Seine Höhe kommt vom Trenner, nicht aus einer festen Zahl: `flexBasis` statt `max-h`,
                damit dieselbe Zahl beide Seiten bestimmt — was er bekommt, fehlt dem Baum. */}
            <div
                // `flexBasis` liegt auf der Hauptachse — bei nebeneinander ist das die Breite,
                // bei übereinander die Höhe. Dieselbe Zahl bestimmt also beide Fälle, und was der
                // eine Bereich bekommt, fehlt dem anderen.
                style={embedded || waagerecht ? { flexBasis: `${detailShare * 100}%` } : undefined}
                className={cn(
                    'flex min-w-0 shrink-0 grow-0 flex-col gap-2 rounded-lg border p-3',
                    embedded || waagerecht ? 'min-h-0 overflow-hidden' : 'overflow-y-auto',
                )}
            >
                {!selected ? (
                    <div className="text-sm text-muted-foreground">{t('portfolioDashboard.dataRoom.pickFile')}</div>
                ) : (
                    <>
                        <div className="flex min-w-0 shrink-0 items-start gap-2">
                            <span className="mt-0.5 shrink-0">{fileIconForName(nameOfPath(selected.path))}</span>
                            <span className="min-w-0 break-words text-sm font-medium">{nameOfPath(selected.path)}</span>
                        </div>

                        {/* Der Blick in die Datei steht direkt unter ihrem Namen — vor allem, was
                            die Analyse über sie sagt.
                            Darunter stand er zwar auch, aber nur für eine gewöhnliche Datei sichtbar:
                            Im Analysefall schieben Abzeichen und Typzeile ihn unter die Kante des
                            Bereichs, und dann ist die Vorschau nur noch für den da, der scrollt.
                            Was der Browser von sich aus zeichnen kann — Bilder und PDFs. */}
                        {previewKind ? (
                            <div className={cn('min-w-0', embedded && 'min-h-0 flex-1')}>
                                <FileThumbnail
                                    path={selected.path}
                                    size={selected.size}
                                    fill={embedded}
                                    onOpen={setViewerUrl}
                                />
                            </div>
                        ) : (
                            <></>
                        )}

                        {/* Der Rest läuft für sich, damit er der Vorschau den Platz nicht nimmt. */}
                        <div
                            className={cn(
                                'flex min-w-0 flex-col gap-2',
                                embedded &&
                                    (previewKind
                                        ? 'max-h-[45%] shrink-0 overflow-y-auto'
                                        : 'min-h-0 flex-1 overflow-y-auto'),
                            )}
                        >
                        {STATE_STYLE[selectedFile.state] ? (
                            <Badge variant={STATE_STYLE[selectedFile.state]!.variant} className="self-start">
                                {t(STATE_STYLE[selectedFile.state]!.key)}
                            </Badge>
                        ) : (
                            // Keine Analyse ist kein Mangel: eine gewöhnliche Datei, abgelegt über die
                            // Dateiverwaltung, und als solche auch löschbar.
                            <span className="text-xs text-muted-foreground">
                                {t('portfolioDashboard.dataRoom.plainFile')}
                            </span>
                        )}

                        {/* Warum er gescheitert ist — die Frist ist der häufigste Grund.
                            Ein roter Punkt allein ist eine Sackgasse: Wer ihn sieht, hat keinen
                            Zugang zum Serverprotokoll. */}
                        {selectedFile.state === 'failed' && (selectedFile.analysis as any)?.failureMessage ? (
                            <p className="text-xs leading-snug text-muted-foreground">
                                {(selectedFile.analysis as any).failureMessage}
                            </p>
                        ) : (
                            <></>
                        )}

                        {selectedLabel ? (
                            <div className="flex items-center gap-1.5 text-sm">
                                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                                {selectedLabel}
                            </div>
                        ) : (
                            <></>
                        )}

                        {/* Was ohne jede Analyse über die Datei feststeht. Jede Zeile fehlt, wo der
                            Wert fehlt — eine Zeile „Größe: unbekannt" sagt weniger als keine. */}
                        {facts.length > 0 ? (
                            <dl className="mt-1 flex flex-col gap-1 rounded-md bg-muted/50 p-2 text-xs">
                                {facts.map(([label, value]) => (
                                    <div key={label} className="flex items-baseline gap-2">
                                        <dt className="shrink-0 text-muted-foreground">{label}</dt>
                                        <dd className="ml-auto min-w-0 truncate text-right">{value}</dd>
                                    </div>
                                ))}
                            </dl>
                        ) : (
                            <></>
                        )}

                        {/* Die Zusammenfassung entsteht beim Aufbereiten für die Suche und steht
                            danach am Dokument. Sie ist das Nächste an einer Vorschau, das ohne
                            Herunterladen der Datei zu haben ist — und in einer schmalen Spalte
                            nützlicher als ein Vorschaubild. */}
                        {selected.summary ? (
                            <div className="mt-1 flex flex-col gap-1">
                                <span className="text-xs font-medium text-muted-foreground">
                                    {t('portfolioDashboard.dataRoom.summary')}
                                </span>
                                <p className="whitespace-pre-line text-xs leading-snug">{selected.summary}</p>
                            </div>
                        ) : (
                            <></>
                        )}

                        {/* Woran das Dokument gebunden hat — erst ab „analysiert".
                            Vorher gibt es keine Bindungen, und ein gescheiterter Lauf hat keine
                            mehr zu erwarten: In beiden Fällen wäre eine Überschrift über nichts
                            eine Aussage, die es nicht gibt. */}
                        {selectedFile.state === 'analysed' && selectedFile.analysis ? (
                            <BoundObjectsPanel analysisId={(selectedFile.analysis as any)._id} />
                        ) : (
                            <></>
                        )}

                        {selectedFile.locked ? (
                            <div className={cn('flex items-start gap-1.5 text-xs text-muted-foreground')}>
                                <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                                {t('portfolioDashboard.dataRoom.lockedHint')}
                            </div>
                        ) : (
                            <></>
                        )}

                        {/* Offene Entscheidungen anzeigen und beantworten. */}
                        {selectedDecisions.length > 0 && onDecide ? (
                            <div className="mt-1 flex flex-col gap-1.5">
                                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                                    <HelpCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                    {t('portfolioDashboard.dataRoom.openDecisions', { count: selectedDecisions.length })}
                                </div>
                                {selectedDecisions.map((decision, index) => (
                                    <Button
                                        key={index}
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="justify-start text-left"
                                        onClick={() => onDecide(decision)}
                                    >
                                        {decision.term}
                                    </Button>
                                ))}
                            </div>
                        ) : (
                            <></>
                        )}

                        {/*
                          * Dass etwas ansteht — in einer Zeile, wie die Hinweise ringsum.
                          *
                          * Die Schaltfläche dazu steht weiter unten, neben ihrem Gegenstück. Hier
                          * steht nur, was zu erwarten ist: wie viele Änderungen und wie viele neuen
                          * Objekte. Damit weiss man vor dem Öffnen, ob sich das Lesen lohnt, und der
                          * Plan muss dafür nicht geladen werden — die Zusammenfassung hängt schon an
                          * der Zeile.
                          */}
                        {selectedMerge ? (
                            <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
                                <ArrowDownToLine className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                                <span>
                                    {t('portfolioDashboard.dataRoom.mergeReady')}
                                    {selectedMerge.mergeSummary
                                        ? ` · ${t('portfolioDashboard.dataRoom.mergeCounts', {
                                              updates: selectedMerge.mergeSummary.updates,
                                              creates: selectedMerge.mergeSummary.creates,
                                          })}`
                                        : ''}
                                </span>
                            </div>
                        ) : (
                            <></>
                        )}

                        {/*
                          * Was mit dem Befund geschehen kann — in einer Zeile, nicht in zwei Balken.
                          *
                          * Die beiden gehören zusammen und schliessen einander aus: Man nimmt an,
                          * was das Dokument sagt, oder man wirft es weg. Untereinander über die
                          * volle Breite gelesen wirkten sie wie zwei getrennte Angebote, von denen
                          * jedes für sich um Aufmerksamkeit bittet. Nebeneinander sind sie das, was
                          * sie sind: die Wahl an dieser Datei.
                          *
                          * Farblich getrennt, weil die Richtung verschieden ist. Übernehmen ist der
                          * Weg nach vorn und trägt Farbe; verwerfen ist der Rückweg — gedämpft, ohne
                          * Fläche, und erst rot, wenn der Zeiger darauf steht.
                          *
                          * Verwerfen trifft die Analyse, nicht die Datei: Wer einen Befund für
                          * falsch hält, will ihn los und die Datei behalten. Danach ist die Zeile
                          * eine gewöhnliche Datei und wieder löschbar.
                          */}
                        {(selectedMerge && onMerge) || (selectedFile.analysis && onRemoveAnalysis) ? (
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                                {selectedMerge && onMerge ? (
                                    <Button
                                        type="button"
                                        size="sm"
                                        className="h-7 gap-1.5 px-2.5 text-xs"
                                        onClick={() => onMerge(selectedMerge)}
                                    >
                                        <ArrowDownToLine className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                        {t('portfolioDashboard.dataRoom.mergeAction')}
                                    </Button>
                                ) : (
                                    <></>
                                )}
                                {selectedFile.analysis && onRemoveAnalysis ? (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 gap-1.5 px-2.5 text-xs text-muted-foreground hover:text-destructive"
                                        onClick={() => {
                                            void Promise.resolve(
                                                onRemoveAnalysis((selectedFile.analysis as any)._id),
                                            ).then(() => {
                                                setAnalysisRefreshKey((k) => k + 1);
                                                void refresh({ force: true });
                                            });
                                        }}
                                    >
                                        <Trash2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                        {t('portfolioDashboard.dataRoom.removeAnalysisAction')}
                                    </Button>
                                ) : (
                                    <></>
                                )}
                            </div>
                        ) : (
                            <></>
                        )}
                        </div>
                    </>
                )}
            </div>
            </div>

            {/* Das Dokument in ganzer Größe. Er zeichnet nur, was das Vorschaubild schon geladen
                hat — deshalb steht er hier und nicht im Detailbereich, dessen Maß der Trenner
                bestimmt. */}
            <DocumentViewerDialog
                url={viewerUrl ?? undefined}
                name={selected ? nameOfPath(selected.path) : ''}
                kind={previewKind}
                onClose={() => setViewerUrl(null)}
            />
        </div>
    );
}
